import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Megaphone, Plus, Trash, PencilSimple, ArrowLeft, FloppyDisk,
  CircleNotch, Eye, Cursor, Timer, ArrowDown, Monitor, X, Upload
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ===== BANNER CONFIG =====
const BannerConfig = ({ storeId }) => {
  const [banner, setBanner] = useState({
    enabled: false, text: '', link_url: '', link_text: '', bg_color: '#0052FF', text_color: '#ffffff', dismissable: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!storeId) return;
    axios.get(`${API_URL}/api/stores/${storeId}/banner`, { withCredentials: true })
      .then(res => setBanner(prev => ({ ...prev, ...res.data })))
      .catch(() => {});
  }, [storeId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/stores/${storeId}/banner`, banner, { withCredentials: true });
      toast.success('Bandeau sauvegardé');
    } catch { toast.error('Erreur'); } finally { setSaving(false); }
  };

  return (
    <Card data-testid="banner-config">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Megaphone size={18} />Bandeau top header</CardTitle>
          <Switch checked={banner.enabled} onCheckedChange={v => setBanner(p => ({ ...p, enabled: v }))} data-testid="banner-toggle" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-xs">Texte du bandeau</Label>
          <Input value={banner.text} onChange={e => setBanner(p => ({ ...p, text: e.target.value }))} placeholder="Livraison offerte dès 50 EUR !" data-testid="banner-text" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Lien (URL)</Label>
            <Input value={banner.link_url || ''} onChange={e => setBanner(p => ({ ...p, link_url: e.target.value }))} placeholder="/promotions" />
          </div>
          <div>
            <Label className="text-xs">Texte du lien</Label>
            <Input value={banner.link_text || ''} onChange={e => setBanner(p => ({ ...p, link_text: e.target.value }))} placeholder="En profiter" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Couleur de fond</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={banner.bg_color} onChange={e => setBanner(p => ({ ...p, bg_color: e.target.value }))} className="w-8 h-8 rounded border cursor-pointer" />
              <Input value={banner.bg_color} onChange={e => setBanner(p => ({ ...p, bg_color: e.target.value }))} className="font-mono text-xs" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Couleur du texte</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={banner.text_color} onChange={e => setBanner(p => ({ ...p, text_color: e.target.value }))} className="w-8 h-8 rounded border cursor-pointer" />
              <Input value={banner.text_color} onChange={e => setBanner(p => ({ ...p, text_color: e.target.value }))} className="font-mono text-xs" />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={banner.dismissable} onCheckedChange={v => setBanner(p => ({ ...p, dismissable: v }))} />
          <Label className="text-xs">Fermable par le visiteur</Label>
        </div>

        {/* Preview */}
        {banner.text && (
          <div className="rounded-lg overflow-hidden mt-2">
            <div className="py-2 px-4 text-center text-sm font-medium flex items-center justify-center gap-2" style={{ backgroundColor: banner.bg_color, color: banner.text_color }}>
              {banner.text}
              {banner.link_text && <span className="underline text-xs">{banner.link_text}</span>}
              {banner.dismissable && <X size={14} className="ml-2 opacity-60" />}
            </div>
          </div>
        )}

        <Button onClick={handleSave} disabled={saving} className="bg-[#0052FF]" data-testid="save-banner-btn">
          {saving ? <CircleNotch className="animate-spin mr-2" size={16} /> : <FloppyDisk className="mr-2" size={16} />}
          Sauvegarder le bandeau
        </Button>
      </CardContent>
    </Card>
  );
};

// ===== POPUP FORM =====
const PopupForm = ({ popup, storeId, onSave, onCancel }) => {
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: popup?.name || '',
    title: popup?.title || '',
    content_html: popup?.content_html || '',
    image_url: popup?.image_url || '',
    cta_text: popup?.cta_text || '',
    cta_url: popup?.cta_url || '',
    trigger: popup?.trigger || 'delay',
    delay_seconds: popup?.delay_seconds || 5,
    scroll_percent: popup?.scroll_percent || 50,
    show_on_pages: popup?.show_on_pages || [],
    frequency: popup?.frequency || 'once',
    is_active: popup?.is_active ?? true,
    bg_color: popup?.bg_color || '#ffffff',
    text_color: popup?.text_color || '#1a1a1a',
    overlay_color: popup?.overlay_color || 'rgba(0,0,0,0.5)',
    position: popup?.position || 'center',
    start_date: popup?.start_date || '',
    end_date: popup?.end_date || '',
  });
  const [saving, setSaving] = useState(false);

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(p => ({ ...p, image_url: res.data.url }));
    } catch { toast.error('Erreur upload'); }
  };

  const handleSave = async () => {
    if (!form.name || !form.title) { toast.error('Nom et titre requis'); return; }
    setSaving(true);
    try {
      if (popup?.id) {
        await axios.put(`${API_URL}/api/stores/${storeId}/popups/${popup.id}`, form, { withCredentials: true });
        toast.success('Popup mis à jour');
      } else {
        await axios.post(`${API_URL}/api/stores/${storeId}/popups`, form, { withCredentials: true });
        toast.success('Popup créé');
      }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const triggers = [
    { value: 'delay', label: 'Après un délai', icon: Timer },
    { value: 'exit_intent', label: 'Exit intent', icon: Cursor },
    { value: 'scroll', label: 'Au scroll', icon: ArrowDown },
    { value: 'page_load', label: 'Au chargement', icon: Monitor },
  ];

  return (
    <div className="space-y-6" data-testid="popup-form">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onCancel}><ArrowLeft size={20} /></Button>
          <h2 className="text-xl font-bold">{popup?.id ? 'Modifier le popup' : 'Nouveau popup'}</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={form.is_active} onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))} />
            <Label className="text-sm">{form.is_active ? 'Actif' : 'Inactif'}</Label>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[#0052FF]" data-testid="save-popup-btn">
            {saving ? <CircleNotch className="animate-spin mr-2" size={16} /> : <FloppyDisk className="mr-2" size={16} />}
            Sauvegarder
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom interne *</Label>
                  <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Popup newsletter" data-testid="popup-name" />
                </div>
                <div>
                  <Label>Titre affiché *</Label>
                  <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="10% de réduction !" data-testid="popup-title" />
                </div>
              </div>
              <div>
                <Label>Contenu HTML</Label>
                <textarea
                  value={form.content_html}
                  onChange={e => setForm(p => ({ ...p, content_html: e.target.value }))}
                  className="w-full min-h-[150px] border rounded-lg p-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="<p>Inscrivez-vous à notre newsletter...</p>"
                />
              </div>
              <div>
                <Label>Image</Label>
                <div className="flex items-center gap-3 mt-1">
                  {form.image_url && (
                    <img src={form.image_url.startsWith('/uploads') ? `${API_URL}${form.image_url}` : form.image_url} alt="" className="h-16 rounded object-contain" />
                  )}
                  <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                    <Upload size={14} className="mr-1" />Uploader
                  </Button>
                  {form.image_url && <Button variant="ghost" size="sm" onClick={() => setForm(p => ({ ...p, image_url: '' }))}><Trash size={14} /></Button>}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUploadImage} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Texte CTA</Label>
                  <Input value={form.cta_text || ''} onChange={e => setForm(p => ({ ...p, cta_text: e.target.value }))} placeholder="J'en profite !" />
                </div>
                <div>
                  <Label>URL CTA</Label>
                  <Input value={form.cta_url || ''} onChange={e => setForm(p => ({ ...p, cta_url: e.target.value }))} placeholder="/promotions" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <CardHeader><CardTitle className="text-base">Déclencheur</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {triggers.map(t => (
                  <button
                    key={t.value}
                    onClick={() => setForm(p => ({ ...p, trigger: t.value }))}
                    className={`p-2.5 rounded-lg border text-xs font-medium flex flex-col items-center gap-1 transition-colors ${form.trigger === t.value ? 'bg-blue-50 border-blue-300 text-blue-700' : 'hover:bg-slate-50'}`}
                  >
                    <t.icon size={18} />
                    {t.label}
                  </button>
                ))}
              </div>
              {form.trigger === 'delay' && (
                <div>
                  <Label className="text-xs">Délai (secondes)</Label>
                  <Input type="number" value={form.delay_seconds} onChange={e => setForm(p => ({ ...p, delay_seconds: parseInt(e.target.value) || 0 }))} />
                </div>
              )}
              {form.trigger === 'scroll' && (
                <div>
                  <Label className="text-xs">% de scroll</Label>
                  <Input type="number" value={form.scroll_percent} onChange={e => setForm(p => ({ ...p, scroll_percent: parseInt(e.target.value) || 0 }))} />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Affichage</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Fréquence</Label>
                <select value={form.frequency} onChange={e => setForm(p => ({ ...p, frequency: e.target.value }))} className="w-full border rounded-lg p-2 text-sm">
                  <option value="once">Une seule fois</option>
                  <option value="session">Par session</option>
                  <option value="always">Toujours</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Position</Label>
                <select value={form.position} onChange={e => setForm(p => ({ ...p, position: e.target.value }))} className="w-full border rounded-lg p-2 text-sm">
                  <option value="center">Centre</option>
                  <option value="bottom_right">Bas droite</option>
                  <option value="bottom_left">Bas gauche</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Date début</Label>
                  <Input type="date" value={form.start_date || ''} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Date fin</Label>
                  <Input type="date" value={form.end_date || ''} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Style</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Fond popup</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <input type="color" value={form.bg_color} onChange={e => setForm(p => ({ ...p, bg_color: e.target.value }))} className="w-8 h-8 rounded border cursor-pointer" />
                    <Input value={form.bg_color} onChange={e => setForm(p => ({ ...p, bg_color: e.target.value }))} className="font-mono text-[10px]" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Texte popup</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <input type="color" value={form.text_color} onChange={e => setForm(p => ({ ...p, text_color: e.target.value }))} className="w-8 h-8 rounded border cursor-pointer" />
                    <Input value={form.text_color} onChange={e => setForm(p => ({ ...p, text_color: e.target.value }))} className="font-mono text-[10px]" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ===== MAIN PAGE =====
const PopupBannerPage = () => {
  const { currentStore } = useStore();
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPopup, setEditingPopup] = useState(null);

  const fetchPopups = useCallback(async () => {
    if (!currentStore) return;
    try {
      const res = await axios.get(`${API_URL}/api/stores/${currentStore.id}/popups`, { withCredentials: true });
      setPopups(res.data);
    } catch {} finally { setLoading(false); }
  }, [currentStore]);

  useEffect(() => { fetchPopups(); }, [fetchPopups]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce popup ?')) return;
    try {
      await axios.delete(`${API_URL}/api/stores/${currentStore.id}/popups/${id}`, { withCredentials: true });
      toast.success('Popup supprimé');
      fetchPopups();
    } catch { toast.error('Erreur'); }
  };

  if (!currentStore) return <div className="text-center py-12 text-slate-500">Sélectionnez une boutique</div>;

  if (editingPopup !== null) {
    return (
      <PopupForm
        popup={editingPopup === 'new' ? null : editingPopup}
        storeId={currentStore.id}
        onSave={() => { setEditingPopup(null); fetchPopups(); }}
        onCancel={() => setEditingPopup(null)}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="popup-banner-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Bandeau & Popups</h1>
        <p className="text-sm text-slate-500 mt-1">Configurez le bandeau top-header et les popups marketing</p>
      </div>

      {/* Banner Section */}
      <BannerConfig storeId={currentStore.id} />

      {/* Popups Section */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Popups</h2>
        <Button onClick={() => setEditingPopup('new')} className="bg-[#0052FF]" data-testid="new-popup-btn">
          <Plus className="mr-1" size={16} /> Nouveau popup
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><CircleNotch className="animate-spin mx-auto" size={24} /></div>
          ) : popups.length === 0 ? (
            <div className="p-12 text-center">
              <Megaphone size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Aucun popup</p>
              <Button onClick={() => setEditingPopup('new')} variant="outline" className="mt-4">
                <Plus className="mr-1" size={14} /> Créer un popup
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Déclencheur</TableHead>
                  <TableHead>Fréquence</TableHead>
                  <TableHead>Vues</TableHead>
                  <TableHead>Clics</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {popups.map(p => (
                  <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setEditingPopup(p)}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {p.trigger === 'delay' ? `Délai ${p.delay_seconds}s` :
                         p.trigger === 'exit_intent' ? 'Exit intent' :
                         p.trigger === 'scroll' ? `Scroll ${p.scroll_percent}%` : 'Chargement'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm capitalize">{p.frequency === 'once' ? 'Une fois' : p.frequency === 'session' ? 'Par session' : 'Toujours'}</TableCell>
                    <TableCell><span className="text-sm font-medium">{p.views || 0}</span></TableCell>
                    <TableCell><span className="text-sm font-medium">{p.clicks || 0}</span></TableCell>
                    <TableCell>
                      <Badge variant={p.is_active ? 'default' : 'secondary'}>{p.is_active ? 'Actif' : 'Inactif'}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setEditingPopup(p); }}><PencilSimple size={16} /></Button>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}><Trash size={16} className="text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PopupBannerPage;
